import React, { useState } from 'react';
import { ShieldCheck, CreditCard, Lock, Heart } from 'lucide-react';
import { CountdownTimer } from './CountdownTimer';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { UpsellPopup } from './UpsellPopup';
import { useCurrencyConversion } from '../hooks/useCurrencyConversion';
export function BuyBoxSection() {
  const {
    ref,
    isVisible
  } = useScrollReveal();
  const [showUpsell, setShowUpsell] = useState(false);
  const {
    localPrice,
    currencyCode,
    countryCode,
    isLoading: currencyLoading
  } = useCurrencyConversion(16.93);
  // Convert 2-letter country code to flag emoji
  const countryFlag = countryCode ? String.fromCodePoint(...countryCode.toUpperCase().split('').map(c => 0x1f1e6 + c.charCodeAt(0) - 65)) : null;
  // URLs
  const ORIGINAL_CART_URL = 'https://www.mundomamis.com/cart/44004522983533:1';
  const BUNDLE_CART_URL = 'https://www.mundomamis.com/cart/44006576881773:1';
  const handleAddToCartClick = (e: React.MouseEvent) => {
    e.preventDefault();
    // Track initial AddToCart event
    if (typeof window !== 'undefined' && (window as any).fbq) {
      ;
      (window as any).fbq('track', 'AddToCart', {
        content_name: 'MegaKit Lectura Feliz',
        content_type: 'product',
        value: 16.93,
        currency: 'USD'
      });
    }
    // Show upsell popup instead of navigating
    setShowUpsell(true);
  };
  const handleUpsellAccept = () => {
    // Track upsell AddToCart event
    if (typeof window !== 'undefined' && (window as any).fbq) {
      ;
      (window as any).fbq('track', 'AddToCart', {
        content_name: 'MegaKit Crianza Positiva (Upsell)',
        content_type: 'product',
        value: 9.99,
        currency: 'USD'
      });
    }
    // Navigate to bundle cart
    window.location.href = BUNDLE_CART_URL;
  };
  const handleUpsellDecline = () => {
    // Navigate to original cart
    window.location.href = ORIGINAL_CART_URL;
  };
  return <section id="buy-section" className="pt-10 pb-4 px-4 bg-white" data-id="element-38">
      <UpsellPopup isOpen={showUpsell} onClose={handleUpsellDecline} onAccept={handleUpsellAccept} data-id="element-39" />

      <div className="max-w-xl mx-auto" data-id="element-40">
        <div ref={ref} className={`flex flex-col items-center ${isVisible ? 'reveal-visible' : 'reveal-hidden'}`} data-id="element-41">
          {/* Product Image */}
          <div className="w-full mb-6" data-id="element-42">
            <img src="https://cdn.magicpatterns.com/uploads/hyGSDgF8CiC1YBJRLFoPMV/Diseno_sin_titulo.png" alt="MegaKit Lectura Feliz - Más de 100 actividades divertidas" className="w-full h-auto" data-id="element-43" />
          </div>

          {/* Social proof line */}
          <p className="text-xs tracking-[0.15em] uppercase text-gray-500 font-medium mb-3" style={{
          fontSize: '8px',
          paddingTop: '0px',
          paddingBottom: '0px',
          paddingLeft: '28px',
          paddingRight: '28px',
          marginTop: '0px',
          marginBottom: '0px',
          marginLeft: '0px',
          marginRight: '0px'
        }} data-id="element-44">
            +3.300 descargas de madres y padres responsables
          </p>

          {/* Product Title */}
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-5 text-center" data-id="element-45">
            MegaKit Lectura Feliz
          </h2>

          {/* Pricing */}
          <div className="flex items-baseline gap-3 mb-2" data-id="element-46">
            <span className="text-lg text-gray-400 line-through" data-id="element-47">$33 USD</span>
            <span className="text-3xl font-bold text-gray-900" data-id="element-48">$16,93 USD</span>
          </div>

          {/* Local currency estimate */}
          {!currencyLoading && localPrice && currencyCode && <div className="text-center mb-4" data-id="element-49">
              <p className="text-sm text-gray-500" data-id="element-50">
                {countryFlag && <span className="mr-1" data-id="element-51">{countryFlag}</span>}≈{' '}
                <span className="font-semibold text-gray-700" data-id="element-52">
                  {localPrice}
                </span>
                <span className="text-xs text-gray-400 ml-1" data-id="element-53">(estimado)</span>
              </p>
              <p className="text-[10px] text-gray-400 mt-0.5" data-id="element-54">
                💳 El cobro se realiza en tu moneda local al momento del pago
              </p>
            </div>}
          {currencyLoading && !localPrice && <div className="h-8 mb-4" data-id="element-55" />}

          {/* Offer badge */}
          <div className="bg-gray-900 text-white px-5 py-2.5 rounded-full text-sm font-semibold mb-8 inline-flex items-center gap-2" data-id="element-56">
            <span data-id="element-57">🎁</span>
            <span data-id="element-58">Oferta -50% OFF sólo 20 Cupos</span>
          </div>

          {/* Green CTA Button with pulse effect */}
          <style data-id="element-59">{`
            @keyframes greenPulse {
              0%, 100% {
                box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.5);
              }
              50% {
                box-shadow: 0 0 0 12px rgba(76, 175, 80, 0);
              }
            }
            .btn-green-pulse {
              animation: greenPulse 2s ease-in-out infinite;
            }
            .btn-green-pulse:hover {
              animation: none;
              box-shadow: 0 8px 25px rgba(76, 175, 80, 0.4);
            }
          `}</style>
          <a href={ORIGINAL_CART_URL} onClick={handleAddToCartClick} className="btn-green-pulse w-full bg-[#4CAF50] hover:bg-[#43A047] text-white text-xl font-bold py-5 px-8 rounded-full shadow-lg transform transition-all duration-200 hover:-translate-y-1 hover:scale-[1.02] active:scale-95 active:translate-y-0 mb-8 block text-center cursor-pointer" data-id="element-60">
            Agregar al carrito
          </a>

          {/* Urgency text */}
          <p className="text-2xl md:text-3xl font-extrabold text-gray-800 mb-6 text-center" style={{
          fontSize: '14px',
          fontWeight: '400'
        }} data-id="element-61">
            <span className="text-yellow-500" data-id="element-62">⚡</span> ¡Últimos 3 Cúpos en
            Oferta! <span className="text-yellow-500" data-id="element-63">⚡</span>
          </p>

          {/* Countdown Timer */}
          <div className="w-full mb-6" data-id="element-64">
            <CountdownTimer data-id="element-65" />
          </div>

          {/* Trust elements */}
          <div className="w-full border-t border-gray-100 pt-6 space-y-4" data-id="element-66">
            <div className="flex items-start gap-3" data-id="element-67">
              <Heart className="w-5 h-5 text-gray-400 shrink-0 mt-0.5" data-id="element-68" />
              <p className="text-sm text-gray-600" data-id="element-69">
                <span className="font-semibold" data-id="element-70">Garantía del corazón:</span>{' '}
                Nuestra promesa como madres y padres
              </p>
            </div>

            <div className="flex items-start gap-3" data-id="element-71">
              <ShieldCheck className="w-5 h-5 text-green-500 shrink-0 mt-0.5" data-id="element-72" />
              <p className="text-sm text-gray-600" data-id="element-73">
                Garantía de satisfacción de 30 días
              </p>
            </div>

            <div className="flex items-center justify-center gap-3 mt-4 opacity-60" data-id="element-74">
              <div className="flex items-center gap-1 text-xs font-bold border border-gray-300 rounded px-2 py-1" data-id="element-75">
                <CreditCard className="w-3 h-3" data-id="element-76" /> VISA
              </div>
              <div className="flex items-center gap-1 text-xs font-bold border border-gray-300 rounded px-2 py-1" data-id="element-77">
                <CreditCard className="w-3 h-3" data-id="element-78" /> MC
              </div>
              <div className="flex items-center gap-1 text-xs font-bold border border-gray-300 rounded px-2 py-1" data-id="element-79">
                PayPal
              </div>
            </div>

            <div className="flex items-center justify-center gap-2 text-xs text-gray-400" data-id="element-80">
              <Lock className="w-3 h-3" data-id="element-81" />
              <span data-id="element-82">Pago 100% seguro y encriptado</span>
            </div>
          </div>
        </div>
      </div>
    </section>;
}